import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuth } from "../../../contexts/AuthContext";
import WhatsAppPreview from "./WhatsAppPreview";

const META_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "gu", label: "Gujarati" },
  { code: "mr", label: "Marathi" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "bn", label: "Bengali" },
  { code: "ur", label: "Urdu" },
];

/* ---------------- INPUT COMPONENTS ---------------- */
const TemplateNameInput = ({ value, onChange }) => (
  <div>
    <input
      className="w-full border rounded p-2"
      value={value}
      onChange={onChange}
      maxLength={512}
      placeholder="e.g. seasonal_sale_01"
    />
    <p className="text-xs text-gray-500 mt-1">Only lowercase letters, numbers, and underscores are allowed.</p>
  </div>
);

const HeaderTextInput = ({ value, onChange }) => (
  <div>
    <input
      className="w-full border rounded p-2"
      value={value}
      onChange={onChange}
      maxLength={60}
    />
    <p className="text-xs text-gray-500 mt-1 text-right">{value.length}/60 characters</p>
  </div>
);

const countEmojis = (str) => {
  const matches = str.match(/\p{Extended_Pictographic}/gu);
  return matches ? matches.length : 0;
};

const BodyTextArea = ({ value, onChange }) => {
  const emojiCount = countEmojis(value);
  return (
    <div>
      <textarea
        className={`w-full border p-2 rounded ${emojiCount > 10 ? 'border-red-500 outline-none ring-1 ring-red-500' : ''}`}
        rows={5}
        value={value}
        onChange={onChange}
        maxLength={1024}
      />
      <div className="flex justify-between text-xs mt-1">
        <span className={emojiCount > 10 ? 'text-red-500 font-medium' : 'text-gray-500'}>
          Emojis: {emojiCount}/10
        </span>
        <span className="text-gray-500">
          {value.length}/1024 characters
        </span>
      </div>
    </div>
  );
};

const FooterInput = ({ value, onChange }) => (
  <div>
    <input
      className="w-full border rounded p-2"
      value={value}
      onChange={onChange}
      maxLength={60}
    />
    <p className="text-xs text-gray-500 mt-1 text-right">{value.length}/60 characters</p>
  </div>
);

/* ---------------- STEP INDICATOR ---------------- */
const steps = ["Set up template", "Edit template", "Submit for review"];

const Stepper = ({ currentStep }) => (
  <div className="flex items-center justify-between mb-8">
    {steps.map((label, index) => {
      const step = index + 1;
      return (
        <div key={step} className="flex items-center flex-1">
          <div
            className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold
              ${
                currentStep === step
                  ? "bg-blue-600 text-white"
                  : currentStep > step
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
          >
            {step}
          </div>
          <span className="ml-3 text-sm font-medium">{label}</span>
          {step < 3 && (
            <div
              className={`flex-1 h-0.5 mx-4 ${
                currentStep > step ? "bg-green-600" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

/* ---------------- STEP 1 UI ---------------- */
const StepOne = ({ category, setCategory, setCurrentStep }) => (
  <div className="bg-white border rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-4">Set up your template</h2>

    <div className="grid grid-cols-3 gap-4">
      {["MARKETING", "UTILITY", "AUTHENTICATION"].map((c) => (
        <button
          key={c}
          onClick={() => setCategory(c)}
          className={`p-4 rounded-lg border font-medium ${
            category === c
              ? "border-blue-600 bg-blue-50"
              : "border-gray-300"
          }`}
        >
          {c}
        </button>
      ))}
    </div>

    <div className="flex justify-end mt-6">
      <button
        onClick={() => setCurrentStep(2)}
        className="px-6 py-2 bg-blue-600 text-white rounded"
      >
        Next →
      </button>
    </div>
  </div>
);

/* ---------------- STEP 2 UI ---------------- */
const StepTwo = ({ 
  templateName, setTemplateName,
  language, setLanguage,
  mediaType, setMediaType,
  headerText, setHeaderText,
  body, setBody,
  samples, setSamples,
  uniqueVars,
  footer, setFooter,
  buttons, setButtons,
  buttonCounts,
  uploadMedia,
  wrap,
  setCurrentStep
}) => (
<div className="space-y-6">
  <div className="bg-white border rounded-lg p-6 grid grid-cols-2 gap-6">
    <div>
      <label className="block text-sm font-medium mb-1">Template name</label>
      <TemplateNameInput
        value={templateName}
        onChange={(e) => {
          // Enforce lowercase and only allow letters, numbers, and underscores
          const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
          setTemplateName(val);
        }}
      />
    </div>

    <div>
      <label className="block text-sm font-medium mb-1">Language</label>
      <select
        className="w-full border p-2 rounded"
        value={language}
        onChange={e => setLanguage(e.target.value)}
      >
        {META_LANGUAGES.map(l => (
          <option key={l.code} value={l.code}>
            {l.label}
          </option>
        ))}
      </select>
    </div>
  </div>

      <div className="bg-white p-6 border rounded">
                <label>Media sample</label>
                <select
                  className="w-full border p-2 rounded mb-3"
                  value={mediaType}
                  onChange={e => setMediaType(e.target.value)}
                >
                  <option value="NONE">No Media</option>
                  <option value="IMAGE">Image</option>
                  <option value="VIDEO">Video</option>
                  <option value="DOCUMENT">Document</option>
                </select>

                {mediaType !== "NONE" && (
                  <input
                    type="file"
                    onChange={e => uploadMedia(e.target.files[0])}
                    accept={
                      mediaType === "IMAGE" ? "image/*" :
                      mediaType === "VIDEO" ? "video/*" :
                      mediaType === "DOCUMENT" ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" :
                      "*/*"
                    }
                  />
                )}
              </div>

      {mediaType === "NONE" && (
        <div className="bg-white border rounded-lg p-6">
          <label className="block text-sm font-medium mb-1">Header</label>
          <HeaderTextInput
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
          />
        </div>
      )}

     <div className="bg-white p-6 border rounded">
                <label>Body</label>
                <BodyTextArea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                />

                {/* TOOLBAR */}
                <div className="flex gap-3 mt-2 text-sm">
                  <button onClick={() => wrap("*")}>B</button>
                  <button onClick={() => wrap("_")}>I</button>
                  <button onClick={() => wrap("~")}>S</button>
                  <button onClick={() => wrap("```")}>Mono</button>
                  <button onClick={() => setBody(body + " 😊")}>😊</button>
                  <button onClick={() => setBody(body + ` {{${uniqueVars.length + 1}}}`)}>
                    + Variable
                  </button>
                </div>
              </div>
              
                {/* VARIABLE SAMPLES */}
              {uniqueVars.length > 0 && (
                <div className="bg-white p-6 border rounded">
                  <h4 className="font-medium mb-3">Variable samples</h4>
                  {uniqueVars.map(v => (
                    <div key={v} className="flex gap-2 mb-2">
                      <input value={v} disabled className="w-20 border p-2" />
                      <input
                        className="flex-1 border p-2"
                        placeholder="Sample value"
                        onChange={e =>
                          setSamples({ ...samples, [v]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

      <div className="bg-white border rounded-lg p-6">
        <label className="block text-sm font-medium mb-1">Footer</label>
        <FooterInput
          value={footer}
          onChange={(e) => setFooter(e.target.value)}
        />
      </div>
      
      <div className="bg-white border rounded-lg p-6">
  <h3 className="text-sm font-medium mb-3">Buttons (Optional)</h3>

  {/* ADD BUTTON CONTROLS */}
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
    <button
      onClick={() => {
        setButtons([
          ...buttons,
          { type: "QUICK_REPLY", text: "" }
        ]);
      }}
      className="px-3 py-2 bg-blue-600 text-white rounded text-sm"
      disabled={buttons.length >= 10}
    >
      + Custom
    </button>
    
    <button
      onClick={() => {
        setButtons([
          ...buttons,
          { type: "URL", text: "", url: "" }
        ]);
      }}
      className="px-3 py-2 bg-green-600 text-white rounded text-sm"
      disabled={buttons.length >= 10 || buttonCounts.visit_website >= 2}
    >
      + Visit Website {buttonCounts.visit_website >= 2 ? '(Max)' : `(${buttonCounts.visit_website}/2)`}
    </button>
    
    <button
      onClick={() => {
        setButtons([
          ...buttons,
          { type: "PHONE_NUMBER", text: "", phone_number: "" }
        ]);
      }}
      className="px-3 py-2 bg-purple-600 text-white rounded text-sm"
      disabled={buttons.length >= 10 || buttonCounts.call_phone_number >= 1}
    >
      + Call Phone {buttonCounts.call_phone_number >= 1 ? '(Max)' : `(${buttonCounts.call_phone_number}/1)`}
    </button>
    
    <button
      onClick={() => {
        setButtons([
          ...buttons,
          { type: "COPY_OFFER_CODE", text: "", code: "" }
        ]);
      }}
      className="px-3 py-2 bg-orange-600 text-white rounded text-sm"
      disabled={buttons.length >= 10 || buttonCounts.copy_offer_code >= 1}
    >
      + Copy Offer Code {buttonCounts.copy_offer_code >= 1 ? '(Max)' : `(${buttonCounts.copy_offer_code}/1)`}
    </button>
  </div>

  <div className="text-xs text-gray-600 mb-4">
    Total buttons: {buttons.length}/10
  </div>

  <div className="space-y-4">
    {buttons.map((btn, index) => (
      <div
        key={index}
        className="border rounded p-4 space-y-3 bg-gray-50"
      >
        {/* Button Type Label */}
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">
            {btn.type === 'QUICK_REPLY' && '🔘 Custom Button'}
            {btn.type === 'URL' && '🌐 Visit Website'}
            {btn.type === 'PHONE_NUMBER' && '📞 Call Phone Number'}
            {btn.type === 'COPY_OFFER_CODE' && '📋 Copy Offer Code'}
          </div>
          <button
            onClick={() =>
              setButtons(buttons.filter((_, i) => i !== index))
            }
            className="text-red-600 text-sm hover:text-red-800"
          >
            Remove
          </button>
        </div>

        {/* Button Text */}
        <input
          className="w-full border rounded p-2"
          placeholder="Button text (max 25 chars)"
          maxLength={25}
          value={btn.text}
          onChange={(e) => {
            const updated = [...buttons];
            updated[index].text = e.target.value;
            setButtons(updated);
          }}
        />

        {/* URL for Visit Website */}
        {btn.type === "URL" && (
          <input
            className="w-full border rounded p-2"
            placeholder="https://example.com"
            value={btn.url || ""}
            onChange={(e) => {
              const updated = [...buttons];
              updated[index].url = e.target.value;
              setButtons(updated);
            }}
          />
        )}

        {/* PHONE for Call Phone Number */}
        {btn.type === "PHONE_NUMBER" && (
          <input
            className="w-full border rounded p-2"
            placeholder="Phone number (with country code)"
            value={btn.phone_number || ""}
            onChange={(e) => {
              const updated = [...buttons];
              updated[index].phone_number = e.target.value;
              setButtons(updated);
            }}
          />
        )}

        {/* CODE for Copy Offer Code */}
        {btn.type === "COPY_OFFER_CODE" && (
          <input
            className="w-full border rounded p-2"
            placeholder="Offer code (e.g., SAVE20)"
            value={btn.code || ""}
            onChange={(e) => {
              const updated = [...buttons];
              updated[index].code = e.target.value;
              setButtons(updated);
            }}
          />
        )}
      </div>
    ))}
  </div>
</div>

      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep(1)}
          className="px-4 py-2 border rounded"
        >
          Previous
        </button>

        <button
          onClick={() => setCurrentStep(3)}
          className="px-6 py-2 bg-blue-600 text-white rounded"
        >
          Submit for review →
        </button>
      </div>
    </div>
);

export default function CreateCamp() {
  const navigate = useNavigate();
  const { uploadTemplateMedia, createTemplate } = useAuth();

  /* ---------------- STEP CONTROL ---------------- */
  const [currentStep, setCurrentStep] = useState(1);

  /* ---------------- STEP 1 ---------------- */
  const [category, setCategory] = useState("MARKETING");

  /* ---------------- STEP 2 ---------------- */
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en");

  const [mediaType, setMediaType] = useState("NONE"); // NONE | IMAGE | VIDEO | DOCUMENT
  const [headerText, setHeaderText] = useState("");
  const [headerHandle, setHeaderHandle] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);

  const [body, setBody] = useState("");
  /* VARIABLES */
  const variables = useMemo(() => [...body.matchAll(/{{\d+}}/g)].map(v => v[0]), [body]);
  const uniqueVars = useMemo(() => [...new Set(variables)], [variables]);
  const [samples, setSamples] = useState({});

  const [footer, setFooter] = useState("");

  const [buttons, setButtons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Count button types for limits
  const buttonCounts = useMemo(() => {
    const counts = {
      custom: 0,
      visit_website: 0,
      call_phone_number: 0,
      copy_offer_code: 0
    };
    
    buttons.forEach(btn => {
      if (btn.type === 'QUICK_REPLY') counts.custom++;
      else if (btn.type === 'URL') counts.visit_website++;
      else if (btn.type === 'PHONE_NUMBER') counts.call_phone_number++;
      else if (btn.type === 'COPY_OFFER_CODE') counts.copy_offer_code++;
    });
    
    return counts;
  }, [buttons]);

  /* ---------------- MEDIA UPLOAD ---------------- */
  const getUploadedMediaHandle = (data) => {
    const candidates = [
      data?.header_handle,
      data?.media_handle,
      data?.handle,
      data?.h,
      data?.response?.header_handle,
      data?.response?.media_handle,
      data?.response?.handle,
      data?.response?.h,
      data?.response?.response?.header_handle,
      data?.response?.response?.media_handle,
      data?.response?.data?.header_handle,
      data?.response?.data?.media_handle,
    ];

    return candidates.find((value) => typeof value === "string" && value.trim()) || null;
  };

  const uploadMedia = async (file) => {
    if (!file) return;

    setHeaderHandle(null);
    setMediaPreview(URL.createObjectURL(file));

    try {
      const result = await uploadTemplateMedia(file);
      const mediaHandle = getUploadedMediaHandle(result);

      if (result.success && mediaHandle) {
        setHeaderHandle(mediaHandle);
        alert("Media uploaded successfully!");
      } else {
        alert(`Media upload failed: ${result.error || result.warning || "No media handle returned"}`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(`Media upload failed: ${error.message}`);
    }
  };
  
   /* FORMAT HELPERS */
  const wrap = (symbol) => {
    const sel = window.getSelection().toString();
    if (!sel) return;
    setBody(body.replace(sel, `${symbol}${sel}${symbol}`));
  };


  /* ---------------- SUBMIT TEMPLATE ---------------- */
  const handleSubmit = async () => {
    if (isSubmitting) return;

    const components = [];

    if (!templateName.trim()) {
      return alert("Template name is required");
    }
    if (!/^[a-z0-9_]+$/.test(templateName)) {
      return alert("Template name can only contain lowercase letters, numbers, and underscores.");
    }

    if (mediaType !== "NONE" && !headerHandle) {
      return alert("Please upload the media sample before submitting");
    }

    if (mediaType !== "NONE" && headerHandle) {
      components.push({
        type: "HEADER",
        format: mediaType,
        example: { header_handle: [headerHandle] },
      });
    } else if (headerText) {
      components.push({
        type: "HEADER",
        format: "TEXT",
        text: headerText,
      });
    }

    if (!body.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Body Required",
        text: "Body text is required.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }
    
    const emojiCount = body.match(/\p{Extended_Pictographic}/gu)?.length || 0;
    if (emojiCount > 10) {
      await Swal.fire({
        icon: "warning",
        title: "Too Many Emojis",
        text: `The body text can contain a maximum of 10 emojis. You currently have ${emojiCount}.`,
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    // --- Custom Meta Validations ---

    // 1. Header Variables (Max 1, must be {{1}})
    if (mediaType === "NONE" && headerText) {
      const headerVars = headerText.match(/{{\d+}}/g) || [];
      if (headerVars.length > 1 || (headerVars.length === 1 && headerVars[0] !== "{{1}}")) {
        await Swal.fire({
          icon: "warning",
          title: "Invalid Header",
          text: "Text headers can contain a maximum of one variable, and it must be exactly {{1}}.",
          confirmButtonColor: "#16a34a",
        });
        return;
      }
    }

    // 2. Footer Variables (None allowed)
    if (footer && /{{\d+}}/.test(footer)) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid Footer",
        text: "The footer cannot contain any variables.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    // 3. Body Variables Sequential & Non-consecutive
    if (uniqueVars.length > 0) {
      // Check sequential
      const varNumbers = uniqueVars.map(v => parseInt(v.replace(/[{}]/g, ""))).sort((a, b) => a - b);
      let isSequential = true;
      for (let i = 0; i < varNumbers.length; i++) {
        if (varNumbers[i] !== i + 1) {
          isSequential = false;
          break;
        }
      }
      if (!isSequential) {
        await Swal.fire({
          icon: "warning",
          title: "Invalid Variables",
          text: "Body variables must be sequential starting from {{1}} (e.g., {{1}}, {{2}}, {{3}}). You cannot skip numbers.",
          confirmButtonColor: "#16a34a",
        });
        return;
      }

      // Check consecutive (e.g. {{1}}{{2}} or {{1}} {{2}})
      if (/{{\d+}}\s*{{\d+}}/.test(body)) {
        await Swal.fire({
          icon: "warning",
          title: "Invalid Variables",
          text: "You cannot place two variables right next to each other (e.g., {{1}} {{2}}). Please add text between them.",
          confirmButtonColor: "#16a34a",
        });
        return;
      }
    }

    // 4. Button Validations
    for (const btn of buttons) {
      if (btn.type === "URL" && btn.url && !/^https?:\/\//.test(btn.url)) {
        await Swal.fire({
          icon: "warning",
          title: "Invalid Button URL",
          text: `The URL "${btn.url}" must start with http:// or https://`,
          confirmButtonColor: "#16a34a",
        });
        return;
      }
      if (btn.type === "PHONE_NUMBER" && btn.phone_number && !/^\+?\d{10,15}$/.test(btn.phone_number)) {
        await Swal.fire({
          icon: "warning",
          title: "Invalid Phone Number",
          text: `The phone number "${btn.phone_number}" must contain a country code and only digits.`,
          confirmButtonColor: "#16a34a",
        });
        return;
      }
    }
    
    // --- End Meta Validations ---

    const missingSamples = uniqueVars.filter((variable) => !samples[variable]?.trim());
    if (missingSamples.length > 0) {
      await Swal.fire({
        icon: "warning",
        title: "Sample Values Missing",
        text: "Please add sample values for all body variables.",
        confirmButtonColor: "#16a34a",
      });
      return;
    }

    components.push({
      type: "BODY",
      text: body,
      ...(uniqueVars.length && {
        example: { body_text: [uniqueVars.map((variable) => samples[variable])] }
      })
    });

    if (footer) {
      components.push({ type: "FOOTER", text: footer });
    }

    if (buttons.length) {
      const formattedButtons = buttons.map(btn => {
        const buttonObj = {
          type: btn.type,
          text: btn.text
        };
        
        if (btn.type === "URL") {
          buttonObj.url = btn.url;
          if (btn.example) buttonObj.example = btn.example;
        } else if (btn.type === "PHONE_NUMBER") {
          buttonObj.phone_number = btn.phone_number;
        } else if (btn.type === "COPY_OFFER_CODE") {
          buttonObj.code = btn.code;
        }
        
        return buttonObj;
      });
      
      components.push({
        type: "BUTTONS",
        buttons: formattedButtons,
      });
    }

    try {
      setIsSubmitting(true);

      const result = await createTemplate({
        name: templateName.trim(),
        language,
        category,
        components,
      });

      if (result.success) {
        await Swal.fire({
          icon: "success",
          title: "Template Submitted",
          text: "Template submitted for review successfully.",
          confirmButtonColor: "#16a34a",
        });
        navigate("/templates");
      } else {
        let errorMessage = result.error || "Unknown error";
        if (result.details) {
          if (result.details.error?.error_user_msg) {
             errorMessage = result.details.error.error_user_msg;
          } else if (result.details.error?.message) {
             errorMessage = result.details.error.message;
          } else if (result.details.message) {
             errorMessage = result.details.message;
          } else if (typeof result.details === "string") {
             errorMessage = result.details;
          } else if (Array.isArray(result.details.errors)) {
             errorMessage = result.details.errors.map(e => e.message || e.error_message).join(", ");
          } else if (typeof result.details.error === "string") {
             errorMessage = result.details.error;
          }
        }
        await Swal.fire({
          icon: "error",
          title: "Template Creation Failed",
          text: errorMessage,
          confirmButtonColor: "#16a34a",
        });
      }
    } catch (error) {
      console.error("Template creation error:", error);
      
      let errorMessage = error.message || "Something went wrong while creating the template.";
      if (error.details) {
        if (error.details.error?.error_user_msg) {
           errorMessage = error.details.error.error_user_msg;
        } else if (error.details.error?.message) {
           errorMessage = error.details.error.message;
        } else if (error.details.message) {
           errorMessage = error.details.message;
        }
      }

      await Swal.fire({
        icon: "error",
        title: "Template Creation Failed",
        text: errorMessage,
        confirmButtonColor: "#16a34a",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------- STEP 3 UI ---------------- */
  const StepThree = () => (
    <div className="bg-white border rounded-lg p-8 text-center">
      <h2 className="text-xl font-semibold mb-4">
        Ready to submit your template?
      </h2>
      <p className="text-gray-600 mb-6">
        WhatsApp will review your template before approval.
      </p>
      <div className="flex justify-center gap-4">
        <button
          type="button"
          onClick={() => setCurrentStep(2)}
          disabled={isSubmitting}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="inline-flex items-center justify-center gap-2 px-8 py-2 bg-green-600 text-white rounded disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Submitting...
            </>
          ) : (
            "Submit & Finish"
          )}
        </button>
      </div>
    </div>
  );

  /* ---------------- RENDER ---------------- */
  return (
    <div className="p-6">
      <Stepper currentStep={currentStep} />

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        {/* LEFT */}
        <div className="xl:col-span-3">
          {currentStep === 1 && <StepOne category={category} setCategory={setCategory} setCurrentStep={setCurrentStep} />}
          {currentStep === 2 && <StepTwo 
            templateName={templateName} setTemplateName={setTemplateName}
            language={language} setLanguage={setLanguage}
            mediaType={mediaType} setMediaType={setMediaType}
            headerText={headerText} setHeaderText={setHeaderText}
            body={body} setBody={setBody}
            samples={samples} setSamples={setSamples}
            uniqueVars={uniqueVars}
            footer={footer} setFooter={setFooter}
            buttons={buttons} setButtons={setButtons}
            buttonCounts={buttonCounts}
            uploadMedia={uploadMedia}
            wrap={wrap}
            setCurrentStep={setCurrentStep}
          />}
          {currentStep === 3 && <StepThree />}
        </div>

        {/* RIGHT PREVIEW */}
        <div className="xl:col-span-1">
          <div className="sticky top-6">
            <h3 className="text-lg font-semibold mb-4">
              WhatsApp Preview
            </h3>
            <WhatsAppPreview
              templateName={templateName}
              headerType={mediaType === "NONE" ? "TEXT" : mediaType}
              headerText={headerText}
              headerPreview={mediaPreview || headerHandle}
              bodyText={body}
              footerText={footer}
              buttons={buttons}
              samples={samples}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
